import React from 'react';
import { motion } from 'framer-motion';

const About = () => {
  const stats = [
    { value: '15+', label: 'Years Experience' },
    { value: '100+', label: 'Projects Delivered' },
    { value: '3', label: 'Major Cloud Platforms' },
  ];

  return (
    <section className="py-24 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center px-4 py-2 bg-cream rounded-full mb-6 border border-gray-100"
            >
              <span className="text-sm font-medium text-muted-grey">
                About
              </span>
            </motion.div>

            <h2 className="text-5xl md:text-6xl font-bold text-dark-slate mb-6 tracking-tight leading-tight">
              Strategic Vision Meets
              <br />
              <span className="text-muted-blue-grey">Hands-On Execution</span>
            </h2>

            <div className="space-y-4 text-lg text-muted-grey leading-relaxed mb-8">
              <p>
                With <span className="font-semibold text-dark-slate">15+ years of engineering leadership</span>,
                we've led teams building cloud-native solutions for enterprises navigating digital transformation.
              </p>
              <p>
                My approach combines executive-level strategic thinking with deep technical expertise.
                I don't just advise—I architect, build, and deploy solutions that drive measurable business outcomes.
              </p>
              <p>
                Whether you need to integrate AI into your operations, migrate to the cloud, or build
                a world-class engineering team, I bring the leadership and hands-on experience to make it happen.
              </p>
            </div>

            {/* CTA */}
            <motion.a
              href="https://calendar.app.google/ube4SfQSbn6SqRCCA"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-dark-slate text-white rounded-full font-semibold hover:bg-deep-charcoal transition-all shadow-lg hover:shadow-xl inline-block"
            >
              Let's Talk Strategy
            </motion.a>
          </motion.div>

          {/* Right Column - Stats Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="bg-gradient-to-br from-dark-navy to-deep-charcoal rounded-3xl p-12 text-white shadow-2xl">
              {/* Decorative Elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-warm-tan opacity-20 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-muted-blue-grey opacity-20 rounded-full blur-3xl"></div>

              <div className="relative z-10">
                <h3 className="text-3xl font-bold mb-8">
                  Proven Track Record
                </h3>

                <div className="space-y-8">
                  {stats.map((stat, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                      className="border-b border-white/20 pb-6 last:border-0"
                    >
                      <div className="text-5xl font-bold mb-2">{stat.value}</div>
                      <div className="text-gray-300 text-lg">{stat.label}</div>
                    </motion.div>
                  ))}
                </div>

                <div className="mt-10 pt-8 border-t border-white/20">
                  <p className="text-gray-300 leading-relaxed">
                    "Engineering leadership that transforms technical complexity into business advantage."
                  </p>
                  <p className="text-sm text-gray-400 mt-4">
                    — North
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
